import * as React from 'react';
import { observer } from 'mobx-react';
import { SliderView, ISliderSettings } from 'elc-slider';
import { Container } from 'elc-grid';
import styled from 'styled-components';
import { ContentSpacing } from 'elc-base-theme';
import {
    IContentItem,
    ITranslationsOverwrites
} from '../../../exported/product-grid-wrapper/ProductGridWrapper';
import { Product } from '../../domain/entities/Product';
import { GridItem } from '../../domain/entities/GridItem';
import { DEFAULT_CAROUSEL_SETTINGS, gridItemType } from '../../constants/ProductGrid';
import { CollectionItem, CollectionItemType } from '../product-grid/CollectionItem';
import { IShortDescriptionConfig } from '../../interfaces/IProdcat';
import { TOGGLE_TYPE } from '../../constants/AutoReplenish';

export interface IProductCarousel {
    config: {
        products?: Product[];
        gridItems?: GridItem[];
        content?: IContentItem[];
        carouselSettings?: ISliderSettings;
        defaultCarouselSettings?: ISliderSettings;
        hideProductPrice?: boolean;
        hideProductPriceRange?: boolean;
        hideProductBadge?: boolean;
        showSubDisplayName?: boolean;
        hideProductRating?: boolean;
        toutWidth?: number;
        toutHeight?: number;
        ctaType?: string;
        enableNotifyMe?: boolean;
        enableQuickView?: boolean;
        enableMobileQuickView?: boolean;
        hideReadReviewsLink?: boolean;
        enableHoverInteraction?: boolean;
        hideReviewsNumber?: boolean;
        enableAutoReplenishment?: boolean;
        enableShadePicker?: boolean;
        enableSizePicker?: boolean;
        shortDescriptionLocation?: IShortDescriptionConfig;
        enableProductDetailLink?: boolean;
        enableQuickViewShortcut?: boolean;
        enableLearnMoreLink?: boolean;
        autoReplenishOptions: number[];
        autoReplenishToggleType?: TOGGLE_TYPE;
        showProductQuantitySelector?: boolean;
        hideProductPricePerUnit?: boolean;
        translationsOverwrites?: ITranslationsOverwrites;
    };
    className?: string;
    disableFullHeight?: boolean;
}

const SliderViewStyled = styled.div`
    margin-bottom: ${ContentSpacing.space20};

    .slick-track {
        margin-left: 0;
        padding: ${ContentSpacing.space4} 0;
        display: flex;
    }

    .slick-slide {
        & > div {
            height: ${props => (props.theme.disableFullHeight ? 'auto' : '100%')};
        }

        height: auto;
        float: none;
        margin: 0 ${ContentSpacing.space4};
    }

    [dir='rtl'] .slick-track {
        margin-right: 0;
        margin-left: auto;
    }

    .elc-slider-arrow-wrapper {
        &::before {
            content: none;
        }
        &.slick-prev {
            left: -15px;
        }

        [dir='rtl'] &.slick-prev {
            left: 0;
            right: -15px;
        }
    }
`;

@observer
export class ProductCarousel extends React.Component<IProductCarousel> {
    public static displayName: string;

    public overrideCarouselSettings = () => {
        if (!this.props.config.carouselSettings) {
            return this.props.config.defaultCarouselSettings || DEFAULT_CAROUSEL_SETTINGS;
        }

        const {
            responsive: responsiveDefault,
            ...restDefaultProperties
        } = DEFAULT_CAROUSEL_SETTINGS;

        const {
            responsive: responsiveOverrideSettings,
            ...restOverrideProperties
        } = this.props.config.carouselSettings;

        const restSettings = {
            ...restDefaultProperties,
            ...restOverrideProperties
        };

        if (!responsiveOverrideSettings) {
            const settings = {
                ...restSettings,
                responsive: responsiveDefault
            };

            return { ...settings };
        }

        const responsiveSettings = responsiveDefault.map((responsive, index) => ({
            ...responsive,
            ...responsiveOverrideSettings[index],
            settings: {
                ...responsive.settings,
                ...responsiveOverrideSettings[index].settings
            }
        }));

        const finalSettings = {
            ...restSettings,
            responsive: responsiveSettings
        };

        return { ...finalSettings };
    };

    private getBriefProps(item: Product | GridItem) {
        const {
            hideProductBadge,
            showSubDisplayName,
            hideProductPrice,
            hideProductPriceRange,
            hideProductRating,
            ctaType,
            enableNotifyMe,
            enableQuickView,
            enableMobileQuickView,
            hideReadReviewsLink,
            hideReviewsNumber,
            enableAutoReplenishment,
            enableHoverInteraction,
            enableShadePicker,
            enableSizePicker,
            shortDescriptionLocation,
            enableProductDetailLink,
            enableQuickViewShortcut,
            enableLearnMoreLink,
            autoReplenishOptions,
            autoReplenishToggleType,
            showProductQuantitySelector,
            hideProductPricePerUnit,
            translationsOverwrites
        } = this.props.config;

        const briefProps = {
            hideProductBadge,
            showSubDisplayName,
            hideProductPrice,
            hideProductPriceRange,
            hideProductRating,
            ctaType,
            enableNotifyMe,
            enableQuickView,
            enableMobileQuickView,
            productGridHideReadReviewsLink: hideReadReviewsLink,
            hideReviewsNumber,
            enableHoverInteraction,
            enableShadePicker,
            enableSizePicker,
            enableAutoReplenishment,
            shortDescriptionLocation,
            enableProductDetailLink,
            enableQuickViewShortcut,
            enableLearnMoreLink,
            autoReplenishOptions,
            autoReplenishToggleType,
            showProductQuantitySelector,
            hideProductPricePerUnit,
            translationsOverwrites
        };

        return briefProps;
    }

    private renderProductItem = (product: Product) =>
        product && (
            <CollectionItem
                key={`product-item-${product.productId}`}
                id={product.productId}
                collectionItem={CollectionItemType.PRODUCT}
                productBriefProps={{ ...this.getBriefProps(product), product }}
            />
        );

    private renderGridItems(item: GridItem) {
        switch (item.itemType) {
            case gridItemType.product:
            case gridItemType.featuredProduct:
                return item.product ? (
                    <CollectionItem
                        key={`grid-item-${item.itemId}`}
                        id={item.itemId}
                        collectionItem={CollectionItemType.PRODUCT}
                        productBriefProps={{ ...this.getBriefProps(item), product: item.product }}
                    />
                ) : (
                    <></>
                );
            default:
                return (
                    <CollectionItem
                        key={`default-item-${item.itemId}`}
                        id={item.itemId}
                        collectionItem={CollectionItemType.TOUT}
                    />
                );
        }
    }

    public render() {
        const carouselSettings = this.overrideCarouselSettings();
        const {
            disableFullHeight,
            config: { gridItems, products }
        } = this.props;

        let carouselItems: React.ReactElement[] = [];

        if (gridItems && gridItems.length > 0) {
            carouselItems = gridItems.reduce((accumulator: React.ReactElement[], item) => {
                if (item.itemType !== gridItemType.featuredProduct) {
                    accumulator.push(this.renderGridItems(item));
                }

                return accumulator;
            }, []);
        } else if (products && products.length > 0) {
            carouselItems = products.map(product => this.renderProductItem(product));
        }

        return (
            carouselItems.length > 0 && (
                <Container>
                    <SliderViewStyled theme={{ disableFullHeight }}>
                        <SliderView sliderSettings={carouselSettings} items={carouselItems} />
                    </SliderViewStyled>
                </Container>
            )
        );
    }
}

ProductCarousel.displayName = 'ProductCarousel';
